// Import Packages
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChipInput from 'material-ui-chip-input';

// Import Pages
import { TextInput } from '../../../FlowConfigurationInputs/TextInput/NewTextInput';
import VariablePicker from "../../../FlowConfigurationInputs/VariablePicker/VariablePicker";
import FieldLabel from '../../../FlowConfigurationInputs/FieldLabel/FieldLabel';

// Import Recoil
import { useRecoilValue, useSetRecoilState, useRecoilState } from "recoil";
import { initialActions, saveAvailable, changedFields } from "../../../../recoil-store/recoil-state";

// Import Helpers
import { checkTheDifference, addDifference, removeDifference } from '../../../../helpers/helper';

/**
 * @Description This function lets you return component according to the name of the incoming action.
 * @Param       action - object
 * @Param       variables - object variable list
 * @Param       setOpenVariable - open variable function that allows us to open
 * @Returns     Component
 * @Author      ibrahim AKIN
 */
function CreatePivot({ action, variables, setOpenVariable }) {
    const { t } = useTranslation();
    const label = 'CONTENT.CONFIGURATION-FORM.EXCEL.CREATE-PIVOT.';
    // Initial states
    const [row, setRow] = useState(action.fields.rowTags?.value ? action.fields.rowTags.value : []);
    const [col, setCol] = useState(action.fields.colTags?.value ? action.fields.colTags.value : []);
    const [val, setVal] = useState(action.fields.values?.value ? action.fields.values.value : []);
    const [filter, setFilter] = useState(action.fields.filters?.value ? action.fields.filters.value : []);
    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);
    useEffect(() => {
        // Assign initial values
        if (!action.fields.tableRange) {
            action.fields.tableRange = { value: '', isRequired: true };
        }
    }, [action]);

    // Handle add a new tag
    const handleAdd = (chip, tag) => {
        let value;
        if (tag === 'filters') {
            value = [...filter, chip];
            setFilter(value);
        } else if (tag === 'rowTags') {
            value = [...row, chip];
            setRow(value);
        } else if (tag === 'colTags') {
            value = [...col, chip];
            setCol(value);
        } else {
            value = [...val, chip];
            setVal(value);
        }

        changeManagement(value, tag);
        action.fields[tag] = { value };
    }
    // Handle delete a tag
    const handleDelete = (chip, tag) => {
        let value;
        if (tag === 'filters') {
            value = filter.filter((c) => c !== chip);
            setFilter(value);
        } else if (tag === 'rowTags') {
            value = row.filter((c) => c !== chip);
            setRow(value);
        } else if (tag === 'colTags') {
            value = col.filter((c) => c !== chip);
            setCol(value);
        } else {
            value = val.filter((c) => c !== chip);
            setVal(value);
        }
        changeManagement(value, tag);
        action.fields[tag] = { value };
    }

    const changeManagement = (newValue, tag) => {
        const field = initialActionsData[action.id]?.fields[tag];
        const oldValue = !field ? undefined : field.value;
        const cfTemp = JSON.parse(JSON.stringify(changedFieldsState));
        if (!oldValue) {
            addDifference(cfTemp, action, tag, setChangeFields, setSaveAvailable)           
        } else {
            if (checkTheDifference(newValue, oldValue)) {            
                addDifference(cfTemp, action, tag, setChangeFields, setSaveAvailable)
            } else {
                removeDifference(cfTemp, action, tag, setChangeFields, setSaveAvailable)               
            }
        }
    }
    const style = { backgroundColor: '#fff', borderRadius: '5px', border: '1px solid gray', padding: '5px' };
    return (
        <>
            <div className="conf-field-container">
                <div className="conf-field-container">

                    <FieldLabel name={t(label + 'EXCEL-SOURCE')} is_required />
                    <VariablePicker action={action} inputName="excelSource" variables={variables}
                        setOpenVariable={setOpenVariable} filter="data table" isRequired hideRequired />
                    <small className="conf-field-desc"><em>{t('CONTENT.CONFIGURATION-FORM.VARIABLE-HELPER', { type: 'data table' })}</em></small>
                </div>
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'SELECT')} is_required />
                <TextInput action={action} inputName="tableRange" variables={variables}
                    setOpenVariable={setOpenVariable} isRequired hideRequired />
                <small className="conf-field-desc"><em>{t(label + 'SELECT-EG')}</em></small>
            </div>
            <div className="conf-field-container">
                <div className="conf-field-container">
                    <FieldLabel name={t(label + 'FIELDS')} />
                    <small className="conf-field-desc"><em>{t(label + 'TAG-EG')}</em></small>
                </div>
                <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto auto' }}>
                        <div style={{ marginBottom: 10, marginRight: 5 }}>

                            <FieldLabel name={t(label + 'FILTER')} />
                            <ChipInput value={filter} onAdd={(chip) => {
                                handleAdd(chip, 'filters');
                            }}
                                onDelete={(chip, index) => {
                                    handleDelete(chip, 'filters');
                                }}
                                newChipKeyCodes={[188]} style={style} fullWidth
                            />
                        </div>
                        <div style={{ marginBottom: 10, marginLeft: 5 }}>

                            <FieldLabel name={t(label + 'ROW-TAG')} />
                            <ChipInput value={row} onAdd={(chip) => {
                                handleAdd(chip, 'rowTags');
                            }}
                                onDelete={(chip, index) => {
                                    handleDelete(chip, 'rowTags');
                                }}
                                newChipKeyCodes={[188]} style={style} fullWidth
                            />
                        </div>
                        <div style={{ marginRight: 5 }}>

                            <FieldLabel name={t(label + 'COL-TAG')} />
                            <ChipInput value={col} onAdd={(chip) => {
                                handleAdd(chip, 'colTags');
                            }}
                                onDelete={(chip, index) => {
                                    handleDelete(chip, 'colTags');
                                }}
                                newChipKeyCodes={[188]} style={style} fullWidth
                            />
                        </div>
                        <div style={{ marginLeft: 5 }}>

                            <FieldLabel name={t(label + 'VALUES')} />
                            <ChipInput value={val} onAdd={(chip) => {
                                handleAdd(chip, 'values');
                            }}
                                onDelete={(chip, index) => {
                                    handleDelete(chip, 'values');
                                }}
                                newChipKeyCodes={[188]} style={style} fullWidth
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default CreatePivot;
